import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDownTrayIcon, ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import type { Report } from '../../types';
import { downloadReport, generateReportPDF } from '../../lib/apiClient';

interface ReportSummaryProps {
  report: Report | null;
  onUpgradeRequired?: (message?: string) => void;
}

export function ReportSummary({ report, onUpgradeRequired }: ReportSummaryProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const generateMutation = useMutation({
    mutationFn: (reportId: string) => generateReportPDF(reportId),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['report'] });
      // Auto-download after generation
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
      setIsLoading(false);
    },
    onError: (error) => {
      setIsLoading(false);
      if (isAxiosError(error) && error.response?.status === 402) {
        onUpgradeRequired?.(error.response?.data?.message);
      }
    },
  });

  const downloadMutation = useMutation({
    mutationFn: (reportId: string) => downloadReport(reportId),
    onSuccess: (data) => {
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
      setIsLoading(false);
    },
    onError: (error) => {
      setIsLoading(false);
      if (isAxiosError(error) && error.response?.status === 402) {
        onUpgradeRequired?.(error.response?.data?.message);
      }
    },
  });

  const handlePdfAction = (reportId: string, hasGeneratedAt: boolean) => {
    setIsLoading(true);
    if (!hasGeneratedAt) {
      // PDF has never been generated - generate it
      generateMutation.mutate(reportId);
    } else {
      // PDF exists - try to download (if expired, will fail and we can regenerate)
      downloadMutation.mutate(reportId);
    }
  };

  if (!report) {
    return (
      <div className="glass-card p-6 text-sm text-muted-foreground">
        Generate a report to review AI-written summaries, room narratives, and export-ready PDFs.
      </div>
    );
  }

  const latest = report.versions[report.versions.length - 1];

  if (!latest) {
    return (
      <div className="glass-card p-6 text-sm text-muted-foreground">
        Report record exists, but no versions have been generated yet.
      </div>
    );
  }

  const hasGeneratedAt = Boolean(latest.generatedAt);

  return (
    <div className="glass-card space-y-4 p-6">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Current version</p>
        <h3 className="text-lg font-semibold text-foreground">{latest.title}</h3>
        {latest.generatedAt && (
          <p className="text-xs text-muted-foreground">
            PDF generated {new Date(latest.generatedAt).toLocaleString()}
          </p>
        )}
      </div>
      <div className="space-y-3 text-sm text-foreground">
        <section>
          <h4 className="font-medium text-muted-foreground">Summary</h4>
          <p className="mt-1 whitespace-pre-line leading-relaxed">{latest.summary}</p>
        </section>
        {latest.introduction && (
          <section>
            <h4 className="font-medium text-muted-foreground">Introduction</h4>
            <p className="mt-1 whitespace-pre-line leading-relaxed">{latest.introduction}</p>
          </section>
        )}
        {latest.conclusion && (
          <section>
            <h4 className="font-medium text-muted-foreground">Conclusion</h4>
            <p className="mt-1 whitespace-pre-line leading-relaxed">{latest.conclusion}</p>
          </section>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">Version {latest.version}</span>
        
        {/* PDF Action Button */}
        <button
          type="button"
          onClick={() => handlePdfAction(report._id, hasGeneratedAt)}
          disabled={isLoading}
          className="btn-glass btn-primary focus-ring flex items-center gap-1.5 text-xs font-semibold disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              {hasGeneratedAt ? 'Downloading...' : 'Generating...'}
            </>
          ) : hasGeneratedAt ? (
            <>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download PDF
            </>
          ) : (
            <>
              <SparklesIcon className="h-4 w-4" />
              Generate PDF
            </>
          )}
        </button>

        {hasGeneratedAt && (
          <button
            type="button"
            onClick={() => {
              setIsLoading(true);
              generateMutation.mutate(report._id);
            }}
            disabled={isLoading}
            className="btn-glass btn-secondary focus-ring flex items-center gap-1.5 text-xs font-semibold disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
        )}

        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {report.distribution?.shareLinks?.length || 0} share links
        </span>
      </div>
    </div>
  );
}
